import React, { useState, useMemo } from "react";
import {
  Paper,
  Stack,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  InputAdornment,
  Tooltip,
  Skeleton,
  Chip,
  alpha,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import SearchIcon from "@mui/icons-material/Search";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";

import InfoButton from "../Clinical/InfoButton";
import {
  getCellColor,
  formatNumber,
  CENTER_THRESHOLDS,
  SP_DIFF_THRESHOLDS,
} from "../../constants/ceoDashboard";

/**
 * Column definitions for the center performance table (v2).
 * Includes SP_Visit columns: spNoVisitPct and spVisitDiff.
 */
const COLUMNS = [
  { field: "centerName", label: "Center", width: 140, align: "left" },
  { field: "totalPatients", label: "Patients", width: 80, align: "right" },
  { field: "noVisit12PlusPct", label: "No Visit %", width: 90, align: "right" },
  { field: "spNoVisitPct", label: "SP No Visit %", width: 100, align: "right" },
  { field: "spVisitDiff", label: "SP Diff", width: 80, align: "right" },
  { field: "openCareGaps", label: "Open Gaps", width: 80, align: "right" },
  { field: "gapCompliance", label: "Compliance", width: 100, align: "right" },
  { field: "erEvents", label: "ER Events", width: 80, align: "right" },
  { field: "highRiskCount", label: "High Risk", width: 80, align: "right" },
  { field: "inCareManagement", label: "In CM", width: 70, align: "right" },
  { field: "highCostPatients", label: "High Cost", width: 80, align: "right" },
];

/**
 * Panel 4 — Center Performance Comparison with sortable heatmap table.
 * Muestra los centros médicos con métricas clave y heatmap de color.
 * v2: Agrega columnas SP No Visit % y SP Diff.
 *
 * @param {{ data: Object, loading: boolean, helpContent: Object }} props
 */
export default function PanelCenterPerformance({ data, loading, helpContent }) {
  const [sortField, setSortField] = useState("totalPatients");
  const [sortDir, setSortDir] = useState("desc");
  const [search, setSearch] = useState("");

  const sorted = useMemo(() => {
    if (!data?.centers) return [];
    let list = data.centers;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((c) => c.centerName?.toLowerCase().includes(q));
    }
    return [...list].sort((a, b) => {
      const av = a[sortField];
      const bv = b[sortField];
      if (typeof av === "string" && typeof bv === "string") {
        return sortDir === "asc"
          ? av.localeCompare(bv)
          : bv.localeCompare(av);
      }
      return sortDir === "asc" ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
    });
  }, [data, sortField, sortDir, search]);

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  /**
   * Determina el estilo de celda basado en heatmap rules.
   */
  const getCellStyle = (field, value, row) => {
    if (field === "noVisit12PlusPct") {
      const c = getCellColor(value, "noVisitPct");
      if (c) return { bgcolor: c.bg, color: c.text, fontWeight: 600 };
    }
    if (field === "spNoVisitPct") {
      const c = getCellColor(value, "noVisitPct");
      if (c) return { bgcolor: c.bg, color: c.text, fontWeight: 600 };
    }
    if (field === "gapCompliance") {
      const c = getCellColor(value, "gapCompliance");
      if (c) return { bgcolor: c.bg, color: c.text, fontWeight: 600 };
    }
    if (field === "inCareManagement") {
      if (row.inCareManagement === 0 && row.highRiskCount > 0) {
        return { bgcolor: "#FEE2E2", color: "#991B1B", fontWeight: 600 };
      }
      if (
        row.inCareManagement > 0 &&
        row.highRiskCount / row.inCareManagement > CENTER_THRESHOLDS.cmRatio
      ) {
        return { bgcolor: "#FEE2E2", color: "#991B1B", fontWeight: 600 };
      }
    }
    return {};
  };

  /**
   * Retorna el estilo del badge para spVisitDiff según los thresholds.
   */
  const getSpDiffBadge = (value) => {
    if (value >= SP_DIFF_THRESHOLDS.high) {
      return { bgcolor: "#D1FAE5", color: "#065F46" };
    }
    if (value >= SP_DIFF_THRESHOLDS.medium) {
      return { bgcolor: "#FEF3C7", color: "#92400E" };
    }
    return null;
  };

  /**
   * Formatea el valor de celda según la columna.
   */
  const formatCell = (field, value) => {
    if (
      field === "noVisit12PlusPct" ||
      field === "gapCompliance" ||
      field === "spNoVisitPct"
    ) {
      return `${value.toFixed(1)}%`;
    }
    if (
      field === "totalPatients" ||
      field === "openCareGaps" ||
      field === "erEvents" ||
      field === "highRiskCount" ||
      field === "highCostPatients"
    ) {
      return formatNumber(value);
    }
    return value;
  };

  // ── Loading state ──────────────────────────────────────────────
  if (loading) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack spacing={2}>
          <Skeleton variant="text" width="50%" animation="wave" />
          <Skeleton
            variant="rectangular"
            height={300}
            sx={{ borderRadius: 2 }}
            animation="wave"
          />
        </Stack>
      </Paper>
    );
  }

  if (!data) return null;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Stack spacing={2}>
        {/* ── Header ──────────────────────────────────────────── */}
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 36,
              height: 36,
              borderRadius: 2,
              background: "linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)",
              color: "white",
              boxShadow: `0 4px 12px ${alpha("#3B82F6", 0.35)}`,
            }}
          >
            <BusinessIcon fontSize="small" />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={700}>
              Center Performance Comparison
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {search
                ? `${sorted.length} of ${data.totalCenters} medical centers`
                : `${data.totalCenters} medical centers`}
            </Typography>
          </Box>
          <InfoButton helpContent={helpContent} />
        </Stack>

        {/* ── Search ────────────────────────────────────────── */}
        <TextField
          size="small"
          placeholder="Search center..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                </InputAdornment>
              ),
            },
          }}
          sx={{
            maxWidth: 280,
            "& .MuiOutlinedInput-root": { borderRadius: 2 },
          }}
        />

        {/* ── Sortable table ──────────────────────────────────── */}
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small" sx={{ minWidth: 960 }}>
            <TableHead>
              <TableRow>
                {COLUMNS.map((col) => (
                  <TableCell
                    key={col.field}
                    align={col.align}
                    sx={{
                      minWidth: col.width,
                      fontWeight: 600,
                      color: "text.secondary",
                      whiteSpace: "nowrap",
                      borderBottom: "2px solid",
                      borderColor: "divider",
                    }}
                  >
                    <TableSortLabel
                      active={sortField === col.field}
                      direction={sortField === col.field ? sortDir : "asc"}
                      onClick={() => handleSort(col.field)}
                    >
                      {col.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((center) => {
                const hasAlert = Boolean(center.alert);
                return (
                  <TableRow
                    key={center.locationKey}
                    sx={{
                      bgcolor: hasAlert ? "#FEF2F2" : "inherit",
                      "&:hover": { bgcolor: hasAlert ? "#FEE2E2" : "action.hover" },
                      transition: "background-color 0.2s",
                    }}
                  >
                    {COLUMNS.map((col) => {
                      const value = center[col.field];
                      const cellStyle = getCellStyle(col.field, value, center);

                      // Center name column — special rendering
                      if (col.field === "centerName") {
                        return (
                          <TableCell
                            key={col.field}
                            align={col.align}
                            sx={{ minWidth: col.width }}
                          >
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              {hasAlert && (
                                <Tooltip title={center.alert} arrow>
                                  <WarningAmberIcon
                                    sx={{ fontSize: 18, color: "#DC2626" }}
                                  />
                                </Tooltip>
                              )}
                              <Typography
                                variant="body2"
                                fontWeight={hasAlert ? 700 : 500}
                                noWrap
                              >
                                {value}
                              </Typography>
                            </Stack>
                          </TableCell>
                        );
                      }

                      // SP Diff column — special rendering with green arrow + badge
                      if (col.field === "spVisitDiff") {
                        const badge = getSpDiffBadge(value);
                        const diffVisits =
                          (center.spVisitedCount || 0) -
                          (center.avalityVisitedCount || 0);
                        return (
                          <TableCell
                            key={col.field}
                            align={col.align}
                            sx={{ minWidth: col.width }}
                          >
                            <Tooltip
                              title={`Sante Plus recorded ${diffVisits} additional patient visits not captured in Availity`}
                              arrow
                            >
                              {badge ? (
                                <Chip
                                  size="small"
                                  icon={
                                    <ArrowUpwardIcon
                                      sx={{ fontSize: 14, color: badge.color }}
                                    />
                                  }
                                  label={`+${value.toFixed(1)}%`}
                                  sx={{
                                    bgcolor: badge.bgcolor,
                                    color: badge.color,
                                    fontWeight: 700,
                                    fontSize: "0.75rem",
                                    height: 24,
                                  }}
                                />
                              ) : (
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  spacing={0.25}
                                  justifyContent="flex-end"
                                >
                                  <ArrowUpwardIcon
                                    sx={{ fontSize: 14, color: "#10B981" }}
                                  />
                                  <Typography variant="body2" fontWeight={500}>
                                    +{value.toFixed(1)}%
                                  </Typography>
                                </Stack>
                              )}
                            </Tooltip>
                          </TableCell>
                        );
                      }

                      return (
                        <TableCell
                          key={col.field}
                          align={col.align}
                          sx={{
                            minWidth: col.width,
                            ...cellStyle,
                          }}
                        >
                          <Typography variant="body2" fontWeight={cellStyle.fontWeight || 400}>
                            {formatCell(col.field, value)}
                          </Typography>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* ── Footnote ────────────────────────────────────────── */}
        <Typography variant="caption" color="text.secondary">
          Heatmap: Green = good, Yellow = needs attention, Red = critical
        </Typography>
      </Stack>
    </Paper>
  );
}
